import { useEffect, useMemo, useState } from "react";
import {
  articleApi,
  type ArticleUnitSummary,
  type MergeResultWire,
  type ResourceRole,
} from "../api/articleUnits";
import { ApiError } from "../api/types";
import { Button, SelectField } from "../compose/controls";
import { Dialog } from "../compose/Dialog";
import { Repeat2 } from "../compose/Icon";
import { formatBytes } from "../lib/fileIngest";

/**
 * Compose this project from other projects' parts —
 * `POST /api/article-units/:id/parts`.
 *
 * ── A MERGE IS A SWAP, AND THE UI HAS TO SAY SO ────────────────────────────
 * Taking the brief from another project does NOT orphan the brief that is here:
 * the server EXCHANGES the slot, so the document currently in this project's
 * brief slot moves INTO the project the new one came from. Both projects keep
 * all three resources and nothing is deleted. That is not something a client can
 * infer, so nothing is committed until the SERVER has stated it: the Preview
 * step runs the real call with `dry_run: true` (which writes nothing) and this
 * renders the returned plan — what moves in, what is displaced, and which
 * project the displaced document lands in.
 *
 * The commit button is inert until a preview EXISTS FOR THE CURRENT SELECTION.
 * Changing a picker invalidates the plan, because a plan for a selection the
 * user has since changed is a plan for a different merge.
 */

const ROLES: readonly { key: ResourceRole; label: string }[] = [
  { key: "brief", label: "Brief" },
  { key: "article", label: "Article" },
  { key: "research", label: "Research" },
];

/** The wire field each role's source picker fills (`PartsBody`'s declared names). */
const ROLE_FIELD: Record<ResourceRole, "brief_from_unit_id" | "article_from_unit_id" | "research_from_unit_id"> = {
  brief: "brief_from_unit_id",
  article: "article_from_unit_id",
  research: "research_from_unit_id",
};

export function MergeArticlesDialog({
  unit,
  onClose,
  onMerged,
}: {
  unit: ArticleUnitSummary;
  onClose: () => void;
  onMerged: (result: MergeResultWire) => void;
}) {
  const [candidates, setCandidates] = useState<ArticleUnitSummary[] | null>(null);
  const [sources, setSources] = useState<Record<ResourceRole, string>>({
    brief: "",
    article: "",
    research: "",
  });
  const [plan, setPlan] = useState<MergeResultWire | null>(null);
  /** The selection the plan on screen was computed FOR. */
  const [plannedFor, setPlannedFor] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "preview" | "commit">("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    articleApi
      .listMergeCandidates(unit.id)
      .then((rows) => {
        if (!live) return;
        setCandidates(rows);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setLoading(false);
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : "Could not load the projects this one could take parts from.",
        );
      });
    return () => {
      live = false;
    };
  }, [unit.id]);

  const selectionKey = `${sources.brief}|${sources.article}|${sources.research}`;
  const named = ROLES.filter((r) => sources[r.key] !== "");
  const previewFresh = plan !== null && plannedFor === selectionKey;

  const titleOf = useMemo(() => {
    const byId = new Map((candidates ?? []).map((c) => [c.id, c.title]));
    byId.set(unit.id, unit.title);
    return (id: number | string | null | undefined): string =>
      id == null ? "" : (byId.get(String(id)) ?? `project #${id}`);
  }, [candidates, unit.id, unit.title]);

  function pick(role: ResourceRole, value: string) {
    setError(null);
    // A plan computed for a selection the user has since changed is a plan for a
    // different merge — drop it rather than let it authorise this one.
    setPlan(null);
    setPlannedFor(null);
    setSources((s) => ({ ...s, [role]: value }));
  }

  function wireInput(dryRun: boolean) {
    const input: {
      brief_from_unit_id?: number;
      article_from_unit_id?: number;
      research_from_unit_id?: number;
      dry_run: boolean;
    } = { dry_run: dryRun };
    for (const r of ROLES) {
      const v = sources[r.key];
      if (v !== "") input[ROLE_FIELD[r.key]] = Number(v);
    }
    return input;
  }

  async function run(dryRun: boolean) {
    setError(null);
    setBusy(dryRun ? "preview" : "commit");
    try {
      const res = await articleApi.mergeArticleParts(unit.id, wireInput(dryRun));
      setBusy("");
      if (dryRun) {
        setPlan(res);
        setPlannedFor(selectionKey);
        return;
      }
      onMerged(res);
    } catch (err) {
      setBusy("");
      setPlan(null);
      setPlannedFor(null);
      setError(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not work out what this merge would do.",
      );
    }
  }

  const options = [
    { value: "", label: "Leave unchanged" },
    ...(candidates ?? []).map((c) => ({
      value: c.id,
      label: `${c.title} (${c.slug})`,
    })),
  ];

  return (
    <Dialog
      title={`Take parts into “${unit.title}”`}
      subtitle="Each slot is exchanged, never emptied — the document that is here today moves into the project you take from."
      onClose={onClose}
      testid="merge-articles-dialog"
    >
      {error ? (
        <div className="auth__error" role="alert" data-testid="ma-error">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="field__hint" role="status" data-testid="ma-loading">
          Loading the projects in this workspace…
        </p>
      ) : (candidates ?? []).length === 0 ? (
        <p className="field__hint" data-testid="ma-no-candidates">
          There is no other project in this workspace to take parts from.
        </p>
      ) : null}

      {ROLES.map((r) => (
        <SelectField
          key={r.key}
          label={`${r.label} — take from`}
          data-testid={`ma-source-${r.key}`}
          value={sources[r.key]}
          options={options}
          onChange={(e) => pick(r.key, e.target.value)}
        />
      ))}

      {previewFresh && plan ? (
        <div className="field" data-testid="ma-plan">
          <span className="field__label">What this merge will do</span>
          {plan.parts.length === 0 ? (
            <p className="field__hint" data-testid="ma-plan-empty">
              Nothing would move.
            </p>
          ) : null}
          {plan.parts.map((p) => (
            <div
              key={p.role}
              data-testid={`ma-part-${p.role}`}
              style={{
                borderTop: "1px solid var(--norkron-hairline, #2a2a2a)",
                padding: "8px 0",
              }}
            >
              <div className="docrow__title">
                {p.role}: “{p.incoming_title}” ({formatBytes(p.incoming_byte_size)})
                comes in from “{p.from_unit_title}”
              </div>
              <div className="docrow__meta">
                {p.displaced_title ? (
                  <span data-testid={`ma-displaced-${p.role}`}>
                    “{p.displaced_title}” ({formatBytes(p.displaced_byte_size)}) is
                    displaced and moves to “{titleOf(p.displaced_to_unit_id)}” — it
                    is not deleted.
                  </span>
                ) : (
                  <span data-testid={`ma-displaced-${p.role}`}>
                    This slot is empty here, so nothing is displaced.
                  </span>
                )}
              </div>
            </div>
          ))}
          {plan.section_links_removed > 0 ? (
            <p className="field__hint" data-testid="ma-links-removed">
              {plan.section_links_removed} cross-resource link
              {plan.section_links_removed === 1 ? "" : "s"} will be dropped — only
              the link rows, never a section, version or document.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="dialog__actions">
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy !== ""}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="secondary"
          data-testid="ma-preview"
          disabled={busy !== "" || named.length === 0}
          title={
            named.length === 0
              ? "Choose at least one part to take."
              : "Ask the server what this merge would do — it writes nothing."
          }
          onClick={() => run(true)}
        >
          {busy === "preview" ? "Working it out…" : "Preview"}
        </Button>
        <Button
          type="button"
          variant="primary"
          data-testid="ma-commit"
          disabled={busy !== "" || !previewFresh}
          title={
            previewFresh
              ? undefined
              : "Preview first — the server states what moves before anything does."
          }
          onClick={() => run(false)}
          icon={<Repeat2 size={15} strokeWidth={1.5} />}
        >
          {busy === "commit" ? "Merging…" : "Take these parts"}
        </Button>
      </div>
    </Dialog>
  );
}
